import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '@/lib/apiBase';

const AuthContext = createContext(null);

const API = getApiBaseUrl();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const saveTokens = (accessToken, refreshToken) => {
  localStorage.setItem('token', accessToken);
  localStorage.setItem('refresh_token', refreshToken);
};

const clearTokens = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh_token');
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const refreshingRef = useRef(null); // deduplicate concurrent refresh calls

  // Axios interceptor: auto-refresh access token on 401
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh');
        const isLoginEndpoint = originalRequest.url?.includes('/auth/login');

        if (error.response?.status === 401 && !originalRequest._retry && !isRefreshEndpoint && !isLoginEndpoint) {
          originalRequest._retry = true;
          const storedRefresh = localStorage.getItem('refresh_token');
          if (!storedRefresh) {
            clearTokens();
            setToken(null);
            setUser(null);
            return Promise.reject(error);
          }

          // Deduplicate parallel refresh calls
          if (!refreshingRef.current) {
            refreshingRef.current = axios
              .post(`${API}/auth/refresh`, { refresh_token: storedRefresh })
              .then((res) => {
                saveTokens(res.data.access_token, res.data.refresh_token);
                setToken(res.data.access_token);
                return res.data.access_token;
              })
              .catch(() => {
                clearTokens();
                setToken(null);
                setUser(null);
                return null;
              })
              .finally(() => {
                refreshingRef.current = null;
              });
          }

          const newToken = await refreshingRef.current;
          if (!newToken) return Promise.reject(error);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axios(originalRequest);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          clearTokens();
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    saveTokens(access_token, refresh_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    const { access_token, refresh_token, user: userData } = response.data;
    saveTokens(access_token, refresh_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearTokens();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
